import React from "react";
import {Tag} from "antd";
import SEO from "../../components/SEO";

const pageSEOSettings = {
    title: "Release Notes",
};

// Most recent first. type: "new" | "improved" | "fixed"
const RELEASES = [
    {
        version: "Latest",
        date: "14 Sep 2026",
        changes: [
            {type: "new", text: "Added an OCR Configuration admin section to manage the OCR settings applied to each supplier's documents, including the model, locale, pages and query fields."},
        ],
    },
    {
        date: "11 Sep 2026",
        changes: [
            {type: "new", text: "Added a Mailbox Users admin section to control which users have access to each queue mailbox, with search by user email and mailbox."},
            {type: "fixed", text: "The supplier list now has a Search button — entering a supplier name and selecting a country no longer leaves the list unchanged."},
            {type: "fixed", text: "The country filter on the supplier list now lists countries and can be searched."},
            {type: "improved", text: "Vessel Managers can now open the Reports page."},
        ],
    },
    {
        date: "24 Jun 2026",
        changes: [
            {type: "improved", text: "The Failed status filter on Reports now includes both failed-validation and awaiting-processing (Received) documents."},
        ],
    },
    {
        date: "22 Jun 2026",
        changes: [
            {type: "improved", text: "Added labels to the Department and Finance Tax Code dropdowns for clearer selection."},
            {type: "new", text: "Net Value and Finance Tax Code are now mandatory together — entering one requires the other before an invoice can be submitted."},
        ],
    },
    {
        date: "15 Jun 2026",
        changes: [
            {type: "new", text: "Added an Invoice Header Description field to the Invoice Header panel on the Invoice Details page."},
        ],
    },
    {
        date: "8 Jun 2026",
        changes: [
            {type: "improved", text: "Updated the Reviewed Ignore label in the email queues."},
        ],
    },
    {
        date: "31 May 2026",
        changes: [
            {type: "improved", text: "Invoice form is read-only where edits are not permitted."},
            {type: "fixed", text: "Fixed an error that could occur when typing on the Invoice Details page."},
        ],
    },
    {
        date: "25 May 2026",
        changes: [
            {type: "improved", text: "Refreshed the Reports page and popup styling."},
        ],
    },
    {
        date: "19 May 2026",
        changes: [
            {type: "improved", text: "Reworked the UI to be mobile friendly."},
            {type: "new", text: "Added the Is Proforma Invoice flag to the Invoice Header."},
        ],
    },
    {
        date: "18 May 2026",
        changes: [
            {type: "new", text: "Added Finance Tax Code (FNTaxCode) support."},
            {type: "improved", text: "General style and UI updates."},
        ],
    },
    {
        date: "17 May 2026",
        changes: [
            {type: "new", text: "Added Departments to the Invoice Header."},
            {type: "new", text: "Added the VFC notification process and a 60-day grace window for vessel-management changes."},
        ],
    },
    {
        date: "12 May 2026",
        changes: [
            {type: "new", text: "Added handling for vessels that have left management, including date checks during processing."},
        ],
    },
];

const TYPE_META = {
    new: {label: "New", color: "green"},
    improved: {label: "Improved", color: "blue"},
    fixed: {label: "Fixed", color: "gold"},
};

const ReleaseNotes = () => {
    return (
        <div>
            <SEO {...pageSEOSettings}/>

            {/* Page header */}
            <div className="px-4 sm:px-6 py-5 bg-white border-b-2 border-b-[#CDD6DB]">
                <div className="text-[#020A08] text-[28px] font-semibold leading-tight">
                    Release Notes
                </div>
                <div className="text-sm text-gray-500 mt-1">
                    What's new in ValidationStation — recent features, improvements and fixes.
                </div>
            </div>

            <div className="px-4 sm:px-6 py-6 max-w-5xl mx-auto">
                {RELEASES.map((release, i) => {
                    const isLast = i === RELEASES.length - 1;
                    return (
                        <div key={i} className="flex gap-4 sm:gap-6">
                            {/* Left rail — date & version */}
                            <div className="hidden sm:block w-[120px] flex-shrink-0 pt-4 text-right">
                                <div className="text-[14px] font-semibold text-[#020A08] leading-tight">
                                    {release.date}
                                </div>
                                {release.version && (
                                    <span
                                        className="inline-block mt-1.5 text-[11px] font-semibold text-white px-2 py-0.5 rounded-full"
                                        style={{backgroundColor: "var(--primary-color)"}}
                                    >
                                        {release.version}
                                    </span>
                                )}
                            </div>

                            {/* Timeline connector */}
                            <div className="hidden sm:flex flex-col items-center flex-shrink-0">
                                <span
                                    className="w-3 h-3 rounded-full mt-[22px] ring-4 ring-[#F7F8FB]"
                                    style={{backgroundColor: "var(--primary-color)"}}
                                />
                                {!isLast && <span className="w-px flex-1 bg-[#E4E7EE] my-1"/>}
                            </div>

                            {/* Change card */}
                            <section className="flex-1 min-w-0 mb-5 bg-white rounded-lg border border-[#E4E7EE]">
                                {/* Date header — shown on mobile only (left rail is hidden) */}
                                <div className="sm:hidden px-5 py-3 border-b border-[#F1F3F7] flex items-center gap-2.5 flex-wrap">
                                    <div className="text-[15px] font-semibold text-[#020A08]">{release.date}</div>
                                    {release.version && (
                                        <span
                                            className="text-[11px] font-semibold text-white px-2 py-0.5 rounded-full"
                                            style={{backgroundColor: "var(--primary-color)"}}
                                        >
                                            {release.version}
                                        </span>
                                    )}
                                </div>
                                <div className="px-5 py-4">
                                    <ul className="space-y-3">
                                        {release.changes.map((change, j) => {
                                            const meta = TYPE_META[change.type] ?? TYPE_META.improved;
                                            return (
                                                <li key={j} className="flex items-start gap-3">
                                                    <Tag color={meta.color} className="!m-0 mt-0.5 flex-shrink-0 w-[72px] text-center">
                                                        {meta.label}
                                                    </Tag>
                                                    <span className="text-[13.5px] text-[#374152] leading-relaxed">
                                                        {change.text}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </section>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ReleaseNotes;
